<template>
  <div
    :class="`status-bar-info-chart status-bar-info-chart-${size} ${buildCache ? 'build-cache' : ''} ${
      elSize.width < blocksSizes.bigMedium && size !== 'small' ? 'large-medium-width' : elSize.width < blocksSizes.small ? 'small-width' : ''
    }`"
  >
    <q-resize-observer @resize="val => (elSize = val)" />

    <div class="row justify-between status-bar-info-chart-title-container items-center sans-700-26">
      <div
        class="sbic--title ibf-gordita-12-m"
        style="line-height: 20px"
      >
        {{ title }}
      </div>
      <div class="sbic--subtitle sans-700-26 status-bar-info-chart--title">
        {{ String(subtitle).length >= 6 ? millify(subtitle) : subtitle?.toLocaleString() }}
      </div>
    </div>
    <div
      v-if="chartComponent"
      class="status-bar-info-chart--chart-container"
    >
      <div
        class="column sbic--legends-container"
        :style="{ margin: !cloud ? 'auto 0' : undefined }"
      >
        <incredi-hierarchy-view
          v-if="cloud"
          class="sbic--incredi-hierarcy"
          :title="{ text: cloudTitle, value: cloud.value.reduce((acc, cur) => acc + cur.value, 0) }"
          :value="cloud.value"
          :color="cloud.color"
        >
          <template #item="{ item }">
            <div
              v-if="item.text === 'Cloud'"
              class="sbic--incredi-hierarcy--row"
            >
              <p class="gordita-400-11">
                {{ item.text }}
                <i class="uil uil-info-circle status-bar-info-chart--cloud-tooltip-icon">
                  <q-tooltip
                    content-class="tooltip-background"
                    :delay="150"
                  >
                    <div class="gordita-500-11-m">Incredibuild Cloud</div>
                  </q-tooltip>
                </i>
              </p>
              <p class="sans-400-12">{{ item.value }}</p>
            </div>
            <div
              v-else
              class="sbic--incredi-hierarcy--row"
            >
              <p class="gordita-400-11">{{ item.text }}</p>
              <p class="sans-400-12">{{ item.value }}</p>
            </div>
          </template>
        </incredi-hierarchy-view>
        <div class="flex column gap-[2px]">
          <legend-key-value
            v-for="(item, index) in legends"
            :test-id="`status-bar-info-chart-legend--${text}`"
            :key="index"
            :text="item.text"
            :color="item.color"
            :value="item.value"
          />
        </div>
      </div>

      <div class="status-bar-info-chart--chart">
        <component
          v-bind="chartAttrs"
          :is="chartComponent"
          :width="size === 'small' ? 48 : 56"
          :height="size === 'small' ? 48 : 56"
          :style="size === 'small' ? { width: '48px !important', height: '48px !important' } : ''"
        />
      </div>
    </div>
  </div>
</template>

<script>
import millify from 'millify';
import IncrediHierarchyView from '../../IncrediHierarchyView/IncrediHierarchyView.vue';
import PieChart from './charts/pie-chart';
import PolarChart from './charts/polar-chart.vue';
import LegendRow from './legend-row.vue';
import LegendKeyValue from './legend-key-value.vue';

export default {
  components: {
    PolarChart,
    PieChart,
    LegendRow,
    IncrediHierarchyView,
    LegendKeyValue,
  },
  props: {
    chart: { type: String },
    size: {
      type: String,
      required: true,
    },
    legends: {
      type: Array,
      default: () => [],
    },
    datasets: {
      type: Array,
    },
    subtitle: {
      type: [String, Number],
      default: '',
    },
    title: {
      type: [String, Number],
      default: '',
    },
    cloud: {
      type: Object,
    },
    cloudTitle: {
      type: String,
    },
    buildCache: {
      type: Boolean,
    },
  },
  data() {
    return {
      elSize: { width: null, height: null },
      blocksSizes: { bigMedium: 230, small: 208 }, // For hiding chart
    };
  },
  computed: {
    millify() {
      return millify;
    },
    chartComponent() {
      switch (this.chart) {
        case 'pie-chart':
          return PieChart;

        case 'polar-chart':
          return PolarChart;

        default:
          return null;
      }
    },
    chartAttrs() {
      return {
        tooltips: false,
        legend: false,
        chartData: {
          datasets: this.datasets,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.status-bar-info-chart--chart {
  margin: auto 0;
}
.status-bar-info-chart {
  &.status-bar-info-chart-small {
    .status-bar-info-chart--title {
      font-size: 16px;
    }
    .sbic--title {
      font-size: 12px !important;
    }
    &.small-width {
      .status-bar-info-chart--chart {
        display: none !important;
      }
    }
  }
  &.status-bar-info-chart-medium {
    .status-bar-info-chart--title {
      font-size: 20px;
    }
    .sbic--title {
      font-size: 13px !important;
    }
    &.large-medium-width {
      .status-bar-info-chart--chart {
        display: none !important;
      }
    }
  }

  &:not(.build-cache) {
    &.status-bar-info-chart-large {
      .status-bar-info-chart--title {
        font-size: 26px;
      }
      .status-bar-info-chart--chart {
        display: block;
      }
      &.large-medium-width {
        .status-bar-info-chart--chart {
          display: none !important;
        }
      }
      .sbic--title {
        font-size: 14px !important;
      }
    }
  }

  &.build-cache {
    &.status-bar-info-chart-large {
      .status-bar-info-chart--title {
        font-size: 26px;
      }
      .status-bar-info-chart--chart {
        display: block;
      }
      &.large-medium-width {
        .status-bar-info-chart--chart {
          display: none !important;
        }
      }
      .sbic--title {
        font-size: 14px !important;
      }
    }
  }
}

.sbic--incredi-hierarcy {
  color: $-text-text-medium;
  .sbic--incredi-hierarcy--row {
    display: grid;
    grid-template-columns: 64px auto;
  }
  :deep(.incredi-hierarcy--title) {
    color: $-text--dark;
  }
}

.status-bar-info-chart {
  padding: 0 16px 0 16px;
  display: flex;
  flex-direction: column;
  color: $-text--dark;
  @media only screen and (min-width: 1552px) and (max-width: 1920px) {
    padding: 0 16px 0 16px;
  }
  @media only screen and (min-width: 1360px) and (max-width: 1551px) {
    padding: 0 12px 0 12px;
  }
  @media only screen and (min-width: 950px) and (max-width: 1359px) {
    padding: 0 8px 0 8px;
    .status-bar-info-chart--chart {
      height: 48px !important;
      width: 48px !important;
      min-height: 48px !important;
      min-width: 48px !important;
    }
  }
  .status-bar-info-chart-title-container {
    flex-direction: column;
    align-items: flex-start;
    line-height: 32px;
    gap: 2px;
    flex-wrap: nowrap;
  }
  .status-bar-info-chart--chart-container {
    align-items: flex-end;
    display: flex;
    height: 100%;
    width: 100%;
    justify-content: space-between;
    .status-bar-info-chart--legends {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .status-bar-info-chart--chart {
      width: 56px;
      height: 56px;
      min-height: 56px;
      min-width: 56px;
    }
  }

  .status-bar-info-chart--subtitle {
    margin: 8px 0;
  }
}
</style>
